import React, { useMemo } from 'react';
import { Modal, Table, Flex, Typography, Tag, Skeleton } from 'antd';
import { PhoneOutlined, MessageOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { ECreditType } from '@leedpulse-monorepo/enums';
import { IPlan } from '@leedpulse-monorepo/interfaces';
import { useLeedbucksConfig } from '../../../hooks/plans/useLeedbucksConfig';

import styles from './UnderstandPlansModal.module.css';

const { Text, Title, Paragraph } = Typography;

interface UnderstandPlansModalProps {
  open: boolean;
  onClose: () => void;
  plans: IPlan[]; // Only company's owned plans
}

const formatNumber = (num: number): string => num.toLocaleString('fr-FR');

const UnderstandPlansModal: React.FC<UnderstandPlansModalProps> = ({ open, onClose, plans }) => {
  const { callRate, smsRate, receptionistRate, isLoading: configLoading } = useLeedbucksConfig();

  const loading = configLoading;

  const sortedPlans = useMemo(
    () =>
      [...plans].sort(
        (a, b) => (a.credits?.[ECreditType.leedbucks] || 0) - (b.credits?.[ECreditType.leedbucks] || 0)
      ),
    [plans]
  );

  const paygRate = useMemo(
    () => sortedPlans.find((p) => p.payg?.[ECreditType.leedbucks] != null)?.payg?.[ECreditType.leedbucks] ?? 0,
    [sortedPlans]
  );

  const planColumns = [
    {
      title: 'Forfait',
      key: 'name',
      render: (_: unknown, plan: (typeof sortedPlans)[number], index: number) => (
        <Flex align="center" gap={8}>
          <Text strong>{plan.name}</Text>
          {sortedPlans.length > 1 && index === 1 && <Tag color="green">Le plus choisi</Tag>}
        </Flex>
      ),
    },
    {
      title: 'Crédits inclus',
      key: 'credits',
      render: (_: unknown, plan: (typeof sortedPlans)[number]) =>
        `${formatNumber(plan.credits?.[ECreditType.leedbucks] || 0)} crédits`,
    },
    {
      title: 'Équivalent appels',
      key: 'calls',
      render: (_: unknown, plan: (typeof sortedPlans)[number]) => {
        const credits = plan.credits?.[ECreditType.leedbucks] || 0;
        if (callRate === 0) return 'illimités'; // a free action
        return callRate ? `jusqu'à ${formatNumber(Math.floor(credits / callRate))} min` : '-';
      },
    },
    {
      title: 'Équivalent SMS',
      key: 'sms',
      render: (_: unknown, plan: (typeof sortedPlans)[number]) => {
        const credits = plan.credits?.[ECreditType.leedbucks] || 0;
        if (smsRate === 0) return 'illimités';
        return smsRate ? `jusqu'à ${formatNumber(Math.floor(credits / smsRate))} SMS` : '-';
      },
    },
    {
      title: 'Prix',
      key: 'price',
      render: (_: unknown, plan: (typeof sortedPlans)[number]) =>
        plan.price?.formatted || `${plan.price?.value ?? 0} €`,
    },
  ];

  const consumptionGrid = [
    { icon: <PhoneOutlined />, label: "1 min d'appel", credits: callRate },
    { icon: <MessageOutlined />, label: '1 SMS envoyé', credits: smsRate },
    { icon: <CustomerServiceOutlined />, label: '1 min réceptionniste IA', credits: receptionistRate },
  ];

  return (
    <Modal title="Comprendre les forfaits" open={open} onCancel={onClose} footer={null} width={760} maskClosable>
      <Paragraph type="secondary">
        Chaque numéro possède un forfait commun. Les appels, les SMS et le réceptionniste IA prélèvent des
        crédits dans cette même réserve.
      </Paragraph>

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <>
          <Table columns={planColumns} dataSource={sortedPlans} rowKey="id" pagination={false} size="small" />

          <Title level={5} style={{ marginTop: 24 }}>
            Grille de consommation
          </Title>

          <div className={styles.grid}>
            {consumptionGrid.map((row) => (
              <Flex key={row.label} align="center" gap={12} className={styles.gridRow}>
                <Flex align="center" justify="center" className={styles.gridIcon}>
                  {row.icon}
                </Flex>
                <Text style={{ flex: 1 }}>{row.label}</Text>
                <Text strong>{row.credits ?? '-'} crédits</Text>
              </Flex>
            ))}
          </div>

          <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
            Après épuisement du forfait, chaque crédit supplémentaire est facturé{' '}
            {paygRate.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €.
          </Paragraph>
        </>
      )}
    </Modal>
  );
};

export default UnderstandPlansModal;
